import { ChangeDetectionStrategy, Component, input, output, inject, computed, ElementRef } from '@angular/core';
import { DropdownMenu } from './dropdown-menu';
import { ZagButton } from '../button/button';
import { ZagIcon } from '../icon/icon';

/**
 * Dropdown menu item component for individual menu items.
 *
 * Used as child of DropdownMenu component to display action items.
 *
 * @example
 * ```html
 * <zag-dropdown-menu-item (clicked)="onAction()">
 *   Action Item
 * </zag-dropdown-menu-item>
 *
 * <zag-dropdown-menu-item [disabled]="true">
 *   Disabled Item
 * </zag-dropdown-menu-item>
 *
 * <zag-dropdown-menu-item [icon]="'fa-user'" (clicked)="onProfile()">
 *   View Profile
 * </zag-dropdown-menu-item>
 * ```
 */
@Component({
  selector: 'zag-dropdown-menu-item',
  imports: [ZagButton, ZagIcon],
  template: `
    @if (button()) {
      <button zagButton type="button" class="zag-dropdown-menu-item__button-wrapper">
        @if (icon()) {
          <span [zagIcon]="icon()!" class="zag-dropdown-menu-item__icon"></span>
        }
        <span class="zag-dropdown-menu-item__label">
          <ng-content></ng-content>
        </span>
      </button>
    } @else {
      <div class="zag-dropdown-menu-item__content">
        @if (icon()) {
          <span [zagIcon]="icon()!" class="zag-dropdown-menu-item__icon"></span>
        }
        <span class="zag-dropdown-menu-item__label">
          <ng-content></ng-content>
        </span>
      </div>
    }
  `,
  styleUrl: './dropdown-menu-item.scss',
  changeDetection: ChangeDetectionStrategy.OnPush,
  host: {
    class: 'zag-dropdown-menu-item',
    '[class.zag-dropdown-menu-item--disabled]': 'disabled()',
    '[class.zag-dropdown-menu-item--highlighted]': 'isHighlighted()',
    '[class.zag-dropdown-menu-item--button]': 'button()',
    '[attr.role]': '"menuitem"',
    '[attr.tabindex]': 'disabled() ? -1 : 0',
    '[attr.aria-disabled]': 'disabled()',
    '[attr.disabled]': 'disabled() || null',
    '(click)': 'onClick($event)',
    '(mouseenter)': 'onMouseEnter()',
  },
})
export class DropdownMenuItem {
  private readonly dropdownMenu = inject(DropdownMenu, { optional: true });
  private readonly elementRef = inject(ElementRef);

  /** Whether the menu item is disabled */
  readonly disabled = input<boolean>(false);

  /** Optional icon class (e.g., 'fa-user', 'icon-settings') */
  readonly icon = input<string | undefined>(undefined);

  /** Whether to render as a button style (call-to-action appearance) */
  readonly button = input<boolean>(false);

  /** Emitted when the menu item is clicked (not emitted if disabled) */
  readonly clicked = output<void>();

  /** Computed: whether this item is highlighted based on parent's highlighted index */
  readonly isHighlighted = computed(() => {
    if (!this.dropdownMenu) return false;

    const highlightedIndex = this.dropdownMenu.highlightedIndex();
    if (highlightedIndex === -1) return false;

    // Find our index among sibling menu items
    const index = this.getIndexInParent();
    return index !== -1 && index === highlightedIndex;
  });

  /**
   * Get this item's index among siblings in the parent menu.
   * Uses DOM querying to find position.
   */
  private getIndexInParent(): number {
    const nativeElement = this.elementRef.nativeElement;
    const parent = nativeElement.closest('.zag-dropdown-menu__content');
    if (!parent) return -1;

    const items = Array.from(parent.querySelectorAll('zag-dropdown-menu-item'));
    return items.indexOf(nativeElement);
  }

  /**
   * Handle click on menu item
   */
  protected onClick(event: MouseEvent): void {
    if (this.disabled()) {
      event.preventDefault();
      event.stopPropagation();
      return;
    }

    this.clicked.emit();

    // Close parent dropdown menu after click
    if (this.dropdownMenu) {
      this.dropdownMenu.close();
    }
  }

  /**
   * Handle mouse enter for highlighting
   */
  protected onMouseEnter(): void {
    if (this.disabled() || !this.dropdownMenu) return;

    const index = this.getIndexInParent();
    if (index !== -1) {
      this.dropdownMenu.setHighlightedIndex(index);
    }
  }
}
